import { arrayFix } from '../arrays';
/**
 * @description Attaches an event listener to an element for one or more specified event types.
 * This utility simplifies adding the same listener to multiple events (e.g., 'mousedown', 'touchstart').
 *
 * @template T A generic type constraint for the element, ensuring it's an EventTarget type (like Document or Element).
 * @param {T} targetElement The DOM element or node (e.g., Document, HTMLElement, ShadowRoot) to attach the listener to.
 * @param {string | string[]} eventTypes One or more event type names (e.g., 'click', or ['mousedown', 'touchstart']).
 * @param {EventListenerOrEventListenerObject} eventListener The function or object that receives a notification when an event of the specified type occurs.
 * @param {boolean | AddEventListenerOptions} [listenerOptions] An optional object or boolean specifying characteristics about the event listener (e.g., capture, passive, once).
 * @returns {void}
 * @example
 * // Attach a listener to both 'click' and 'keydown'
 * addMultiEvent(document.getElementById('myButton'), ['click', 'keydown'], (e) => {
 * console.log(`${e.type} occurred!`);
 * });
 */
export const addMultiEvent = (targetElement, eventTypes, eventListener, listenerOptions = {}) => {
    const controller = new AbortController();
    if (typeof listenerOptions === 'boolean') {
        listenerOptions = { capture: listenerOptions };
    }
    arrayFix(eventTypes)
        .filter(eventType => !!eventType?.trim?.())
        .forEach(eventType => targetElement.addEventListener(eventType, e => eventListener?.(e), { ...listenerOptions, ...{ signal: controller.signal } }));
    return controller;
};
/**
 * Univerzálne pridá listener udalosti. Podporuje:
 * - DOM elementy (HTML, Window, Document) s inferenciou špecifických udalostí.
 * - Non-DOM API (Stream, WebSocket, XHR) s inferenciou špecifických udalostí.
 *
 * @param target Jeden (T) alebo pole (T[]).
 * @param types Jedna udalosť (K) alebo pole udalostí (K[]).
 * @param listener Funkcia poslucháča s automaticky typovanou udalosťou.
 * @param options Možnosti pre addEventListener.
 */
export const multiEvent = (target, types, listener, options) => {
    const targets = arrayFix(target);
    const eventTypes = arrayFix(types)
        .map(t => String(t)?.trim())
        .filter(t => !!t);
    if (!targets.length || !eventTypes.length)
        return;
    const controller = new AbortController();
    if (typeof options === 'boolean')
        options = { capture: options };
    targets.forEach(element => eventTypes.forEach(type => element.addEventListener(type, listener, { ...options, ...{ signal: controller.signal } })));
    return controller;
};
//# sourceMappingURL=events.js.map